require('../componentStyles/addressLookup.css')
import * as React from 'react';
import * as field from "../../../ap/fields/field";
import apiHeaders from "../../../utils/headerUtils";
import Autosuggest from 'react-autosuggest-ie11-compatible';
import Autowhatever from 'react-autowhatever'
import {config} from "../../../config/config";
import responseErrorHandler from "../../../utils/responseErrorUtils";
import {LabelStyles} from '../../../ap/base/label'
import * as gls from '../../../ap/layout/gls';
import * as typestyle from 'typestyle';
import { appState } from '../../../state/state';
import { v4 as uuidV4 } from 'uuid';

import {
  AddressApiRequest, AddressLookupState, AddressLookupProps, MANUAL_ADDRESS_SUGGESTION,
  AddressLookupSuggestion
} from "./addressLookupTypes";

import {buildAddressLookupTheme} from "./addressLookupTheme";

const KEYCODE_ENTER_KEY = 13;

const LETTERS_AND_WHITESPACE_REGEX = /[a-z\W]+/i;
const CHARACTER_COUNT_MIN = 4;
const minParcelLockerDpid = 21900001;
const maxParcelLockerDpid = 21909999;
const blockedPostalDeliveryTypes = new Set(['PCL LKR', 'PCL COL']);

const isBlockedAddress = (address: any): boolean => {
  const postalDeliveryType = address && address.structured && address.structured.postalDeliveryType;

  if (!!postalDeliveryType) {
    return blockedPostalDeliveryTypes.has(postalDeliveryType);
  }

  const dpid = Number(address && address.dpid);
  return Number.isFinite(dpid) && dpid >= minParcelLockerDpid && dpid <= maxParcelLockerDpid;
};

const {voi} = appState;

export class AddressLookup extends React.Component<AddressLookupProps, AddressLookupState> {

  autosuggestAutowhatever: Autowhatever;
  autosuggestInput: HTMLElement;

  public static defaultProps = {
    state: {
      searchTerm: '',
      suggestionsVisible: false,
      suggestions: []
    } as AddressLookupState,
    takeFocus: false
  } as AddressLookupProps;

  constructor(props) {

    super(props);

    this.state = props.state;
  }

  componentDidMount() {
    if (!!this.props.takeFocus) {
      this.autosuggestInput.focus();
    }
  }

  preventDefault = (e) => {
    e = e || event;
    e.preventDefault();
    e.stopPropagation();
  }

  convertTabToArrowDown = (event) => {
    event.key = 'ArrowDown';
    this.autosuggestAutowhatever.onKeyDown(event);
  }

  handleAutoSuggestKeyDown = (event) => {
    if (event.keyCode === KEYCODE_ENTER_KEY) {
      this.preventDefault(event);
    }
    if (event.key === 'Tab' && this.state.suggestionsVisible) {
      this.convertTabToArrowDown(event);
    }
  }

  // Autosuggest is a controlled component.
  // This means that you need to provide an input value and an onChange handler that updates this value (see below).
  // Suggestions also need to be provided to the Autosuggest, and they are initially empty because the Autosuggest is closed.
  onChange = (event, {newValue, method}) => {

    let userSelectedAnItem = (method === 'enter' || method === 'click');

    if (MANUAL_ADDRESS_SUGGESTION.matches(newValue)) {
      userSelectedAnItem && this.props.switchToManual();
    }

    voi.searchTerm = newValue;
    voi.addressLine1.setValue(voi.searchTerm);
    this.setState({ searchTerm: newValue });
  }

  getAddressSuggestions = (partialAddress) => {

    const body: AddressApiRequest = {
      id: uuidV4(),
      maxResults: 10,
      predictive: true,
      matchBuildingAliases: false,
      fallbackToFuzzySearch: true,
      addresses: [
        {
          id: uuidV4(),
          text: partialAddress
        }
      ],
      detail: {
        singleLine: true,
        semiStructured: false,
        upu: false,
        structured: true,
        geo: false,
        delivery: false
      }
    };

    let options = {
      method: "POST",
      headers: apiHeaders(),
      mode: 'cors' as RequestMode,
      body: JSON.stringify(body)
    };

    let url = config.addressLookupConfig.endpoint + '/ame/api/v1/address';

    fetch(url, options)
      .then(response => responseErrorHandler(response))
      .then(response => response.json())
      .then(json => {
        const addresses = (json.results[0].addresses || []).filter(address => !isBlockedAddress(address));
        this.setState({ suggestionsVisible: addresses.length > 0 });
        return this.replaceSuggestions(addresses);
      })
      .catch(e => {
        console.error('Address lookup search service is currently unavailable - switching to manual entry mode.');
        this.props.lookupFailureHandler();
      });
  }

  getAddressDetails = (dpid) => {

    let options = {
      method: "GET",
      headers: apiHeaders(),
      mode: 'cors' as RequestMode
    };

    let url = config.addressLookupConfig.endpoint + '/ame/api/v1/dpid/' + dpid + '?semiStructured=true';
    fetch(url, options)
      .then(response => responseErrorHandler(response))
      .then(response => response.json())
      .then(addressDetails => this.replaceAddressDetails(addressDetails.results[0].addresses[0]))
      .catch(e => {
        console.error('Address lookup details service is currently unavailable - switching to manual entry mode.');
        this.props.lookupFailureHandler();
      });
  }

  replaceSuggestions = (newSuggestions: AddressLookupSuggestion[]) => {

    newSuggestions.push(MANUAL_ADDRESS_SUGGESTION)
    this.setState({ suggestions: newSuggestions});
  }

  replaceAddressDetails = (addressDetails) => {
    voi.addressLine1.setValue(addressDetails.semiStructured.addressLines[0]);
    voi.addressLine2.setValue(addressDetails.semiStructured.addressLines[1]);
    voi.suburb.setValue(addressDetails.semiStructured.locality);
    voi.state.setValue({ display: '', value: addressDetails.semiStructured.state });
    voi.postcode.setValue(addressDetails.semiStructured.postcode);
    voi.fullClientAddress = addressDetails.singleLine;
    voi.dpid.setValue(addressDetails.dpid);
    if (voi.useClientAddressAsTransacted) {
      voi.propertyBeingTransacted.setValue(voi.fullClientAddress);
    }
    this.setState({ selectedAddressDetail: addressDetails });
  }

  // Autosuggest will call this function every time you need to update suggestions.
  // You already implemented this logic above, so just use it.
  onSuggestionsFetchRequested = ({value}) => {
    this.getAddressSuggestions(value);
  }

  onSuggestionSelected = (event, { suggestion, suggestionValue, suggestionIndex, sectionIndex, method }) => {

    if (MANUAL_ADDRESS_SUGGESTION.matches(suggestion.address)) {
      this.props.switchToManual();
      voi.clearAddressLines();

      let manualAddressLine1 = MANUAL_ADDRESS_SUGGESTION.matches(this.state.searchTerm) ? '' : this.state.searchTerm;
      voi.addressLine1.setValue(manualAddressLine1);

      voi.searchTerm= '';
      voi.addressLookup.validate();
    }
    else {
      let newState = {
        searchTerm: suggestionValue,
        selectedValue: suggestion
      };

      voi.searchTerm= suggestionValue;
      this.setState(newState);
      voi.addressLookup.validate();

      this.getAddressDetails(suggestion.dpid);
    }

    this.setState({suggestionsVisible: false});
  }

  onSuggestionsClearRequested = () => {
    this.replaceSuggestions([]);
    this.setState({ suggestionsVisible: false });
  }

  getSuggestionValue = (suggestion: AddressLookupSuggestion): string => {
    return suggestion.singleLine;
  }

  renderSuggestion = (suggestion: AddressLookupSuggestion) => {
    return (
      <span>{suggestion.singleLine}</span>
    );
  }

  shouldRenderSuggestions = (partialAddress) => {
    let doRender = false;

    // // eg: trim '111 Bourke Street ' down to 'Bourke Street'.
    let alphaPartialAddress = LETTERS_AND_WHITESPACE_REGEX.exec(partialAddress);

    if (!!alphaPartialAddress) {
      doRender =  alphaPartialAddress[0].trim().length >= CHARACTER_COUNT_MIN;
    }

    return doRender
  }

  buildId(innerId: string) {
    return this.props.id + "-" + innerId;
  }

  storeAutosuggestReference = autosuggest => {
    if (autosuggest !== null) {
      this.autosuggestAutowhatever = autosuggest.autowhatever;
      this.autosuggestInput = autosuggest.input;
    }
  };

  render() {

    const { searchTerm, suggestions } = this.state;

    const inputProps = {
      placeholder: "Please type your address here...",
      type: 'search',
      value: voi.searchTerm,
      onChange: this.onChange,
      onKeyDown: this.handleAutoSuggestKeyDown
    };

    const addressLookupTheme = buildAddressLookupTheme(!!voi.addressLookup.error)

    return (
      <div id={this.buildId("address-lookup-fields")} >
        <gls.ContentVerticalContentMargined margin={8}>
          <label htmlFor="address-lookup-suggest" className={LabelStyles.rootClass}>
            Client address
          </label>

          <div data-comment="Field" className={typestyle.classes(field.FieldStyles.rootClassName, this.props.className)}>
            <div className={field.FieldStyles.inputContainerClassName}>
              <Autosuggest
                id={this.buildId("address-lookup-suggest")}
                ref={this.storeAutosuggestReference}
                suggestions={suggestions}
                shouldRenderSuggestions={this.shouldRenderSuggestions}
                renderSuggestion={this.renderSuggestion}
                onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                onSuggestionSelected={this.onSuggestionSelected}
                getSuggestionValue={this.getSuggestionValue}
                inputProps={inputProps}
                className={typestyle.style({ width: '100%' })}
                theme={addressLookupTheme}
              />
            </div>
          </div>
        </gls.ContentVerticalContentMargined>
      </div>
    );
  }
}

export default AddressLookup;
